import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/react-hooks';
import { UPDATE_USER_DISPUTE, RESOLVE_USER_DISPUTE } from "utils/mutations";
import { gql } from 'apollo-boost';
import SettingsInputContainer from "../Settings/common/SettingsInputContainer";
import ReactJson from "react-json-view";
import { Link } from "react-router-dom";

const moment = require("moment");
const parseDateStr = str => str ? moment.unix(parseInt(str) / 1000).format("M/D/YYYY") : "";

const  GALILEO_DISPUTE_REASONS = {
    1: "Funds not dispensed",
    2: "Not as described",
    3: "Cancelled recurring transactions",
    4: "Multiple",
    5: "Non receipt of services or merchandise",
    6: "Duplicate transaction",
    7: "Unauthorized",
    8: "Incorrect amount billed",
    9: "Paid by other means",
    10: "Credit not processed"
};

const QuestionnaireInput = ({ handleQuestionUpdate, setUpdated, que, ans = "", i}) => {
    const [question, setQuestion] = useState(que);
    const [answer, setAnswer] = useState(ans);
    const [changed, setChanged] = useState(false);

    useEffect(() => {
        if (ans !== answer && question) setChanged(true);
    }, [question, answer]);

    const handleClick = () => {
        handleQuestionUpdate(question, answer);
        setChanged(false);
        setUpdated(true);
        if (!que) {
            setQuestion("");
            setAnswer("");
        }
    };

    return (
        <div className="">
            {
                que ? 
                <div className="font-bold my-5">
                    {`${i + 1}. ${que}`}
                </div> : 
                <div className="font-bold my-5">
                    {`${i + 1}.`}
                    <SettingsInputContainer>
                        <input
                            value={question || ""}
                            placeholder="Enter additional question here"
                            type="text"
                            className={`w-full outline-none`}
                            onChange={e => setQuestion(e.target.value)}
                        />
                    </SettingsInputContainer>
                </div>
            }
            <SettingsInputContainer>
                <input
                    value={answer}
                    placeholder="Enter you answer here"
                    type="text"
                    className={`w-full outline-none`}
                    onChange={e => setAnswer(e.target.value)}
                />
            </SettingsInputContainer>
            <button className={changed ? "mt-2" : "hidden"} onClick={handleClick}>Update</button>
        </div>
    );
};

const DisputeData = ({ dispute, isLoading, setShouldRefetch}) => {
    const { email } = dispute.User;
    const { id, disputed_amount, granted_amount, created_at, updated_at, reason_code, status, resolved_at, galileo_status, galile_dispute_id, transaction, metadata, authorization, galileo_auth } = dispute;
    const [updateDispute] = useMutation(UPDATE_USER_DISPUTE);
    const [resolveDispute] = useMutation(RESOLVE_USER_DISPUTE);
    const [amount, setAmount] = useState(disputed_amount || transaction.amount || 0);
    const [questionnaire, setQuestionnaire] = useState(JSON.parse(dispute.questionnaire) || {});
    const [reason, setReason] = useState(dispute.reason_code || "");
    const [updated, setUpdated] = useState(false);
    const [actionError, setActionError] = useState("");
    const [saveError, setSaveError] = useState("");
    const [inputError, setInputError] = useState("");
    const [saved, setSaved] = useState(false);
    const [loading, setLoading] = useState(isLoading || false);
    const parsedMeta = JSON.parse(metadata);
    const parsedAuthorization = JSON.parse(authorization);
    const parsedGalileoAuth = JSON.parse(galileo_auth);

    const userAddress = useQuery(USER_ADDRESS_QUERY, {
        variables: { email: email }
    });

    const handleQuestionUpdate = (q, v) => {
        const tmp = Object.assign(questionnaire);
        tmp[q.toString()] = v;
        setQuestionnaire(ques => tmp);
    };

    const mapQuestionnaireToList = () => (
        [...Object.keys(questionnaire), null].map((q, i) => (
            <div key={q}>
                <QuestionnaireInput 
                    handleQuestionUpdate={handleQuestionUpdate}
                    setUpdated={setUpdated}
                    que={q} 
                    ans={questionnaire[q]} 
                    i={i} 
                />
            </div>
        ))
    );

    const [list, setList] = useState(mapQuestionnaireToList(questionnaire));

    useEffect(() => {
        if (updated){
            setUpdated(false);
            setList(mapQuestionnaireToList(questionnaire));
        }
    }, [updated]);

    useEffect(() => {
        if (saved) {
            setTimeout(() => setSaved(false), 5000);
        };
    }, [saved])

    const validateInput = () => {
        setInputError("");
        setSaveError("");
        
        const parsedAmount = parseFloat(amount);
        if (!reason) {
            setInputError("Please select a dispute reason");
            return false;
        };
        if (!parsedAmount || parsedAmount <= 0 || parsedAmount > transaction.amount) {
            setInputError("Please enter a valid disputed amount");
            return false;
        };
        if (!questionnaire || !Object.values(questionnaire).filter(a => a.length).length) {
            setInputError("Questionnaire cannot be blank");
            return false;
        }
        if (!id) {
            setInputError("Error - please refresh");
            return false;
        };
        return true;
    };

    const handleSave = async () => {
        if (!validateInput()) {
            setSaveError("invalid input - changes not saved");
            return false;
        };

        const payload = {
            dispute_id: id,
            reason_code: reason.length ? parseInt(reason) : null,
            disputed_amount: parseFloat(amount),
            questionnaire: JSON.stringify(questionnaire)
        };

        const res = await updateDispute({ variables: payload })
            .then(
                (res) => {
                    // console.dir(res.data);
                    const { status, message } = res.data.updateUserDispute;
                    if (status === "OK"){
                        console.log("updated");
                        setSaved(true);
                        return true;
                    } else {
                        console.log(message);
                        setSaveError(message);
                        return false;
                    }
                }
            );

        return res;
    };

    const handleResolveDispute = async (action) => {
        setActionError("");

        if (dispute.status && action !== 'unforward') return;
        if (['forward', 'honor'].includes(action)){
            const res = await handleSave();
            if (!res) return;
        };

        if (!window.confirm(`Are you sure to ${action} this dispute?`)) return;

        setLoading(true);
        resolveDispute({
            variables: {
                action,
                dispute_id: id
            }
        })
            .then(
                (res) => {
                    const { status, message } = res.data.resolveDispute;
                    if (status === "OK") {
                        setShouldRefetch(true);
                    } else {
                        console.log(message);
                        setActionError(message);
                    };
                    setLoading(false);
                }
            );
    };

    const galileoStatus = () => {
        if (['dispute_created'].includes(galileo_status)){
            return (
                <span>
                    {galileo_status} <button className="underline hover:text-green-primary" onClick={() => handleResolveDispute("unforward")}>Cancel</button>
                </span>
            );
        };
        return galileo_status;
    };

    return (
        <div className="admin-page">
            <a className="underline hover:text-green-primary"
                href="/dashboard/admin/god/dispute">
                    Back to dispute
            </a>
            <div className="flex flex-row my-8 md:flex-row">
                <div className="flex mb-4 md:mb-0 flex-col md:mx-10">
                    <div className="">
                        <div className="text-2xl font-title mb-2 font-light">
                            Dispute #{dispute.id}
                        </div>

                        <table className="tomo-table">
                            <tbody>
                                <tr>
                                    <th>User</th>
                                    <td>
                                        <div className="flex flex-col">
                                            {email}
                                            <span>
                                                <Link className="mr-5 underline hover:text-green-primary" to={{ pathname: `/admin/god/user?email=${encodeURIComponent(email)}` }}>Godview</Link>
                                                <Link className="underline hover:text-green-primary" to={{ pathname: `/supportal/user?email=${encodeURIComponent(email)}` }}>Supportal</Link>
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <th>Status</th>
                                    <td>{status || "not_reviewed"}</td>
                                </tr>
                                <tr>
                                    <th>Galileo Status</th>
                                    <td>{galileoStatus()}</td>
                                </tr>
                                <tr>
                                    <th>Date created</th>
                                    <td>{parseDateStr(created_at)}</td>
                                </tr>
                                <tr>
                                    <th>Date resolved</th>
                                    <td>{parseDateStr(resolved_at)}</td>
                                </tr>
                                <tr>
                                    <th>Amount Granted</th>
                                    <td>{granted_amount}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className="my-10">
                        <div className="text-2xl font-title mb-2 font-light">
                            Transaction Summary
                        </div>
                        <table className="tomo-table">
                            <tbody>
                                <tr>
                                    <th>Description</th>
                                    <td>{transaction.description}</td>
                                </tr>
                                <tr>
                                    <th>Amount</th>
                                    <td>{transaction.amount}</td>
                                </tr>
                                <tr>
                                    <th>Status</th>
                                    <td>{transaction.status}</td>
                                </tr>
                                <tr>
                                    <th>Category</th>
                                    <td>{transaction.category}</td>
                                </tr>
                                <tr>
                                    <th>Authorized at</th>
                                    <td>{parseDateStr(transaction.created_at)}</td>
                                </tr>
                                <tr>
                                    <th>Settled at</th>
                                    <td>{parseDateStr(transaction.settled_at)}</td>
                                </tr>
                                <tr>
                                    <th>Merchant Location</th>
                                    <td>{transaction.merchant_location}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className={dispute.status ? "hidden" : ""}>
                        <div className="text-2xl font-title mb-2 font-light">
                            Dispute Action
                        </div>
                        <div className="flex mb-4 mx-20 md:mb-0 flex-col md:mx-2 w-2/3">
                            <button
                                className="btn bg-yellow-500 border-none cursor-pointer px-4 py-2 my-2"
                                disabled={!!dispute.status || loading}
                                onClick={() => handleResolveDispute("forward")}>
                                    Submit for Investigation
                            </button>
                            <button
                                className="btn cursor-pointer px-4 py-2 my-2"
                                disabled={!!dispute.status || loading}
                                onClick={() => handleResolveDispute("cancel")}>
                                    Mark as Resolved
                            </button>
                            <button
                                className="btn btn-green cursor-pointer px-4 py-2 my-2"
                                disabled={!!dispute.status || loading}
                                onClick={() => handleResolveDispute("honor")}>
                                    Honor Request
                            </button>
                            <button
                                className="btn bg-red-600 border-0 cursor-pointer px-4 py-2 my-2"
                                disabled={!!dispute.status || loading}
                                onClick={() => handleResolveDispute("deny")}>
                                    Deny Request
                            </button>
                        </div>
                    </div>
                    <div className="text-red-500">
                        {actionError}
                    </div>
                </div>
                <div>
                    <div className="p-2 flex-1">
                        <div className="font-bold">Dispute Metadata</div>
                        <ReactJson
                            src={parsedMeta}
                            name={null}
                            theme="summerfruit:inverted"
                            iconStyle="circle"
                            collapsed={2}
                            displayDataTypes={false}
                            enableClipboard={false}
                        />
                    </div>
                    
                    <div className="p-2 flex-1">
                        <div className="font-bold">User Address Details</div>
                        <ReactJson
                            src={userAddress.data?.getUserAddressDetails}
                            name={null}
                            theme="summerfruit:inverted"
                            iconStyle="circle"
                            collapsed={2}
                            displayDataTypes={false}
                            enableClipboard={false}
                        />
                    </div>
                    
                    <div className="p-2 flex-1">
                        <div className="font-bold">Authorization</div>
                        <ReactJson
                            src={parsedAuthorization}
                            name={null}
                            theme="summerfruit:inverted"
                            iconStyle="circle"
                            collapsed={2}
                            displayDataTypes={false}
                            enableClipboard={false}
                        />
                    </div>
                    
                    <div className="p-2 flex-1">
                        <div className="font-bold">Auth API</div>
                        <ReactJson
                            src={parsedGalileoAuth}
                            name={null}
                            theme="summerfruit:inverted"
                            iconStyle="circle"
                            collapsed={2}
                            displayDataTypes={false}
                            enableClipboard={false}
                        />
                    </div>
                    
                </div>
            </div>
            <div>
                <div className="text-2xl font-title mb-2 font-light md:mx-10">
                    Dispute Info
                </div>

                <table id="dispute-info-tb" className='w-1/2 tomo-table'>
                    <tbody>
                        <tr>
                            <th>Reason for Dispute</th>
                            <td className='w-2/3'>
                                <select 
                                    className="h-8"
                                    name="reason"
                                    defaultValue={reason}
                                    onChange={e => setReason(e.target.value)}
                                    >
                                    <option className="text-right" key={"default"} value={""}>--- Select a reason ---</option>
                                        {Object.keys(GALILEO_DISPUTE_REASONS).map(r => (
                                            <option className="text-right" key={r} value={r}>{`${r}. ${GALILEO_DISPUTE_REASONS[r]}`}</option>
                                        ))}
                                </select>
                            </td>
                        </tr>
                        <tr>
                            <th>Disputed Amount</th>
                            <td className='flex flex-row justify-end'>
                                <span className='w-1/2'>
                                    <SettingsInputContainer>
                                        <input
                                            value={amount}
                                            placeholder="Enter amount"
                                            type="number"
                                            className={`w-full outline-none text-right`}
                                            onChange={e => setAmount(e.target.value)}
                                        />
                                    </SettingsInputContainer>
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div className="text-red-500 my-5">
                    {inputError}
                </div>
                
                <div className="font-bold mt-10">Questionnaire: </div>
                <div className="flex flex-col mb-8 md:flex-row">
                    <div className="flex mb-4 mx-20 md:mb-0 flex-col md:mx-2">
                        {list}
                    </div>
                </div>
                <div className="flex justify-center">
                    <div className="flex flex-col">
                        <button
                            className="btn bg-green-600 border-0 cursor-pointer px-6 py-2 ml-4 w-32"
                            onClick={handleSave}
                            disabled={saved}
                        >
                            {saved ? "Saved" : "Save"}
                    </button>
                        <div className="text-red-500 my-5">
                            {saveError}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};



const DisputeView = (props) => {
    const { disputeId: id } = props.match.params;
    const { loading, error, data, refetch } = useQuery(DISPUTE_QUERY, {
        variables: { disputeId: id }
    });

    const [shouldRefetch, setShouldRefetch] = useState(false);

    const [fetchDispute] = useLazyQuery(DISPUTE_QUERY, {
        variables: { disputeId: id },
        fetchPolicy: "network-only",
        onCompleted: async () => {
            await refetch();
            setShouldRefetch(false);
        }
    });

    useEffect(() => {
        if (shouldRefetch) fetchDispute();
    }, [shouldRefetch]);

    return error ? (
        <>Error.....</>
    ) : loading ? (
        <>Loading...</>
        ) : data.getDispute ? (
            <DisputeData dispute={data.getDispute} isLoading={loading || shouldRefetch} setShouldRefetch={setShouldRefetch}/>
        ) : <>Dispute not found</>;
};

export default DisputeView;

const DISPUTE_QUERY = gql`
  query DisputeDetails($disputeId: ID){
    getDispute(id: $disputeId){
        id
        created_at
        updated_at
        resolved_at
        reason_code
        disputed_amount
        granted_amount
        questionnaire
        status
        galileo_dispute_id
        galileo_status
        outcome
        User {
          email
          first_name
          last_name
        }
        transaction {
            id
            amount
            created_at
            settled_at
            description
            category
            status
            merchant_location
        }
        metadata
        authorization
        galileo_auth
    }
  }
`

const USER_ADDRESS_QUERY = gql`
  query UserAdressDetails($email: String!){    
    getUserAddressDetails(email: $email){
        address_type
        street
        city
        state
        zip
        country
    }
  }
`

